import React, { FC, Fragment, MouseEvent, useEffect, useState } from 'react';
import AddCircleRoundedIcon from '@material-ui/icons/AddCircleRounded';
import { Box, Button, Divider, Grid, Popover } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';

import { ClientDepartmentFragment, ClientLocationFragment } from 'api';

import { UserSearchesV2Filter } from './type';
import { Departments, Locations, Role } from './Partials';

interface Props {
  filter: UserSearchesV2Filter;
  setFilter: (filter: UserSearchesV2Filter) => void;
  clearFilter: () => void;
  setSelectedDepartments: (departments: Array<ClientDepartmentFragment>) => void;
  setSelectedLocations: (locations: Array<ClientLocationFragment>) => void;
}

const useStyles = makeStyles(theme => ({
  button: {
    background: theme.palette.background.default,
    minWidth: 120,

    '&:hover': {
      border: '1px solid transparent',
      background: theme.palette.background.default,
    },
  },
  container: {
    width: 640,
    padding: theme.spacing(4, 4, 2, 4),
  },
  submitButton: {
    marginLeft: theme.spacing(2),
  },
}));

const Filters: FC<Props> = ({
  filter,
  setFilter,
  clearFilter,
  setSelectedDepartments,
  setSelectedLocations,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [tempFilter, setTempFilter] = useState<UserSearchesV2Filter>(filter);

  useEffect(() => {
    if (!isEqual(filter, tempFilter)) {
      setTempFilter(filter);
    }
  }, [filter]);

  function handleReconciliation() {
    setTempFilter(filter);
  }

  function onClickButton(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function onClose() {
    setAnchorEl(null);
  }

  function onSubmit() {
    setFilter({ ...tempFilter });
    onClose();
  }

  function renderButtons() {
    return (
      <Grid item container justify="flex-end">
        <Grid item>
          <Button
            variant="outlined"
            onClick={() => {
              clearFilter();
              onClose();
            }}
            size="large"
          >
            {t('common:clear')}
          </Button>
        </Grid>

        <Grid item>
          <Button
            className={classes.submitButton}
            variant="contained"
            color="primary"
            size="large"
            onClick={onSubmit}
          >
            {t('common:apply')}
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Fragment>
      <Box display="flex" height="100%">
        <Button
          className={classes.button}
          variant="contained"
          size="large"
          onClick={onClickButton}
          endIcon={<AddCircleRoundedIcon color="primary" />}
        >
          {t('analytics:filters')}
        </Button>
      </Box>

      <Popover
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={onClose}
        onExited={handleReconciliation}
        onEnter={handleReconciliation}
      >
        <div className={classes.container}>
          <Grid container spacing={2}>
            <Departments
              filter={tempFilter}
              setFilter={setTempFilter}
              setSelectedDepartments={setSelectedDepartments}
            />

            <Locations
              filter={tempFilter}
              setFilter={setTempFilter}
              setSelectedLocations={setSelectedLocations}
            />

            <Role filter={tempFilter} setFilter={setTempFilter} />

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {renderButtons()}
          </Grid>
        </div>
      </Popover>
    </Fragment>
  );
};

export default Filters;
